/**
 * pRI Chat Store — Zustand state management for the pRI AI assistant panel.
 * Follows the chat-store.ts pattern with persist middleware for session continuity.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Maximum message length to prevent memory pressure */
const MAX_MESSAGE_LENGTH = 2000;

/** Maximum stored messages to prevent unbounded growth */
const MAX_MESSAGES = 200;

export interface PriMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  intent?: string;
  tier?: string;
}

interface PriState {
  messages: PriMessage[];
  isLoading: boolean;
  sessionId: string | null;
  hasWelcomed: boolean;

  // Actions
  addMessage: (msg: Omit<PriMessage, 'id' | 'timestamp'>) => void;
  setLoading: (loading: boolean) => void;
  setSessionId: (id: string) => void;
  markWelcomed: () => void;
  clearHistory: () => void;
}

export const usePriStore = create<PriState>()(
  persist(
    set => ({
      messages: [],
      isLoading: false,
      sessionId: null,
      hasWelcomed: false,

      addMessage: msg => {
        const trimmedContent = msg.content.trim().slice(0, MAX_MESSAGE_LENGTH);
        if (!trimmedContent) return;

        const newMessage: PriMessage = {
          id: `pri-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          role: msg.role,
          content: trimmedContent,
          timestamp: new Date(),
          intent: msg.intent,
          tier: msg.tier,
        };

        set(state => ({
          messages: [...state.messages.slice(-MAX_MESSAGES + 1), newMessage],
        }));
      },

      setLoading: loading => set({ isLoading: loading }),

      setSessionId: id => set({ sessionId: id }),

      markWelcomed: () => set({ hasWelcomed: true }),

      clearHistory: () =>
        set({
          messages: [],
          hasWelcomed: false,
          sessionId: null,
        }),
    }),
    {
      name: 'kgc-pri',
      partialize: state => ({
        messages: state.messages.slice(-50), // Persist only last 50 messages
        sessionId: state.sessionId,
        hasWelcomed: state.hasWelcomed,
      }),
    }
  )
);
